import type { SmartWorkspaceDTO, ViewModeEnum, ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import type { FilterWorkspaceChain } from "../chain";
import type { WorkspaceChain } from "../chain/type";
import type { ParameterizedConceptDTOWithMandatoryUuid, Workspace, WorkspaceChartCtx } from "../ctx/context";
import type { WorkspaceTableSettingsColumnProperty } from "../workspaceTable/WorkspaceTableSettingsColumnProperty";

/**
 * Every mutation the workspace supports, named for what the user did.
 *
 * The vocabulary is the one `__test__/utils.ts` characterizes today's controllers against — the
 * `WorkspaceMutation` union there maps 1:1 onto the types below, which is the evidence that this replaces
 * the mutation layer rather than redesigning it. Two things follow from that mapping and are worth
 * spelling out because they are load-bearing:
 *
 * - **Routing lives in the payload, not in the name.** `addColumn` covers what three controller methods
 *   did (`createLocalColumn`, `createSharableColumn`, `createCustomProperty`); `share` and `property` say
 *   which. Where the concept lands is a consequence of the payload, and the reducer decides it.
 * - **`upsertFilter` does not collapse** into `addFilter`/`editFilter`. Which of the two it performs is
 *   read out of current state (is there already an `in` filter on this concept), not out of caller
 *   intent, so it is an operation in its own right.
 *
 * Split in two unions rather than filtered through a deny-list. `excludeMethods` was a list a new controller
 * method could silently miss; a new action has to be declared as one of the two, and {@link actionSpec} then
 * refuses to compile until its persistence is stated.
 *
 * Two payloads carry a **concept** where today's controller took a uuid — `addFilter` and the two chart
 * operations. That is the shape of the fix for "the caller had already resolved it": a uuid is a promise the
 * reducer cannot check, and a promise it cannot check is how a document ends up referencing nothing.
 */
export type WorkspaceAction = ContentAction | EphemeralAction;

/** Actions that touch the workspace document — the concept pool included. */
export type ContentAction =
	// --- columns ---
	| { type: "addColumn"; chain: WorkspaceChain; share?: boolean }
	| { type: "addColumn"; property: WorkspaceTableSettingsColumnProperty }
	| { type: "editColumn"; parameterizedConceptUuid: string; chain: WorkspaceChain; shared?: boolean }
	| { type: "editColumn"; parameterizedConceptUuid: string; property: WorkspaceTableSettingsColumnProperty }
	/**
	 * Ask for one option of a TAG custom attribute to be renamed, everywhere it is stored.
	 *
	 * Not an `editColumn {property}`: that goes to the update endpoint, which identifies options by name and
	 * would read the rename as the old option having been deleted — stripping the tag from every portfolio
	 * carrying it. The rename endpoint replaces the value in one transaction, in the attribute, on the
	 * portfolios, and in the filters of every workspace of every user of the client.
	 *
	 * **It changes nothing locally.** The local copy of the criterion is rewritten by `tagOptionRenamed`,
	 * once the transaction has committed — see the note there.
	 */
	| { type: "renameTagOption"; parameterizedConceptUuid: string; currentTagValue: string; newTagValue: string }
	| { type: "removeColumn"; parameterizedConceptUuid: string; shared?: boolean }
	/**
	 * `fromGrid` says the columns are a **sample of a layout still in motion**, not one the user has settled
	 * on. That is the difference between the two callers of "the column list changed":
	 *
	 * - the settings panel's drag-and-drop and visibility toggle emit one discrete change per interaction, so
	 *   the plain action persists and tracks;
	 * - AG Grid's own `stateUpdated` — a resize, a header drag, a column hidden from its chooser — emits a
	 *   *stream*: it fires *during* the drag (ag-grid debounces `stateUpdated` by a microtask, not by an
	 *   interval), so persisting every sample would be a whole-document PUT and a MixPanel event per frame.
	 *   `fromGrid` keeps the store in step with the grid without paying either.
	 *
	 * The flag is therefore not "the grid's layout is never saved". The table coalesces the stream and
	 * re-dispatches the settled layout **without** the flag once the grid stops moving (`gridColumns$` in
	 * `workspaceTable/index.tsx`); that dispatch is the one that persists. A layout that reached the store and
	 * nothing else was the bug — a resized or dragged column snapped back on reload.
	 *
	 * Routing in the payload rather than in a second action name, for the reason `addColumn` gives: which one
	 * happened is a property of the caller, not a different operation.
	 */
	| { type: "reorderColumns"; columns: Workspace["columns"]; fromGrid?: boolean }
	// --- filters ---
	/**
	 * `concept` is how a filter on an *existing* concept is expressed — the shared one the panel resolved.
	 * It carries the concept rather than its uuid on purpose: an action that names a concept the pool does
	 * not hold would leave the document referencing nothing, and "the caller had already resolved it" is not
	 * something the reducer can check. Given the concept, it can store it.
	 */
	| { type: "addFilter"; chain: FilterWorkspaceChain; concept?: ParameterizedConceptDTOWithMandatoryUuid }
	| { type: "upsertFilter"; chain: FilterWorkspaceChain }
	| { type: "editFilter"; parameterizedConceptUuid: string; chain: FilterWorkspaceChain }
	| { type: "removeFilter"; parameterizedConceptUuid: string }
	| { type: "removeAllFilters" }
	// --- charts ---
	/**
	 * `thresholdConcept` is the key-metric threshold's own concept, for the same reason `addFilter` takes
	 * one: `chart.relativeThreshold` is a *reference*, and today the panel mints the concept in a separate
	 * call before passing its uuid. Passed here, the operation is one action and the reference resolves by
	 * construction.
	 */
	| {
			type: "addChart";
			concepts: ParameterizedConceptDTO[];
			// `WorkspaceChartCtx` rather than the bare DTO, so a panel can create a chart with a display
			// preference already answered — the density chart's overlay toggles are on its create form, and a
			// chart born with one of them unticked has to be stored that way.
			chart: Omit<WorkspaceChartCtx, "series">;
			thresholdConcept?: ParameterizedConceptDTO;
	  }
	| {
			type: "editChart";
			index: number;
			concepts: ParameterizedConceptDTO[];
			chart: Partial<WorkspaceChartCtx>;
			thresholdConcept?: ParameterizedConceptDTO;
	  }
	| { type: "removeChart"; index: number }
	| { type: "reorderCharts"; charts: WorkspaceChartCtx[] }
	// --- table state that is persisted ---
	| {
			type: "setView";
			view: ViewModeEnum;
			/**
			 * Whether the choice is the user's, and so worth remembering. Default `true`.
			 *
			 * `false` is the deep link (`?tab=`, and what `open_page` sends): landing on a tab SHOWS it
			 * without rewriting the workspace's saved `viewMode` for everyone who opens it next. Clicking
			 * the tab is the user saying which view this workspace has, and still persists.
			 */
			persist?: boolean;
	  }
	| { type: "setSort"; sort: Workspace["sort"] }
	| { type: "setPageSize"; size: number }
	// --- write-behind reconciliation: not user intent, see `WorkspaceEffect` ---
	| { type: "sharedConceptsReplaced"; concepts: ParameterizedConceptDTOWithMandatoryUuid[] }
	| { type: "conceptReconciled"; placeholderUuid: string; concept: ParameterizedConceptDTOWithMandatoryUuid }
	/**
	 * A committed tag-option rename, into this client's copy of the filters that carry the value.
	 *
	 * The rewrite waits for the transaction because the filters are what the grid's data request is keyed on:
	 * rewritten first, the table asks the backend for a value it does not have yet and is answered with no
	 * rows — and that answer sticks, because the invalidation the rename fires afterwards cannot cancel a
	 * query that is on its first fetch (`Query.fetch` only cancels one that already holds data) and dedupes
	 * onto the in-flight request instead.
	 *
	 * Rewriting it at all is not optional: local state is authoritative here and the PUT carries the whole
	 * document, so leaving the old value in memory means the next unrelated edit — a column resize is enough
	 * — persists it back over the rename, for this user's workspace alone.
	 */
	| { type: "tagOptionRenamed"; parameterizedConceptUuid: string; currentTagValue: string; newTagValue: string }
	/**
	 * The upstream document, re-read.
	 *
	 * `meta.document` is seeded once per mount and spread *first* by the serializer, so it is the only home of
	 * the fields this client does not model — `name` and `taxonomy`. Both change through an API of their own:
	 * renaming or moving a workspace, whether from the app drawer, from another tab or through SphereAI, never
	 * touches the document this screen edits. So the seed goes stale while the query that fetched it is
	 * invalidated, refetched, and correct.
	 *
	 * Two things went wrong while nothing carried that refetch back in. The page header's rename dialog opened
	 * on the *old* name, because it reads the store. And the next whole-document PUT — a column resize is
	 * enough — spread the stale `name` back over the rename, which is the half that reached other users.
	 *
	 * Adopting a freshly fetched document is safe with local edits in flight because **content is not read
	 * from here**: the serializer overwrites `parameterizedConcepts`, `charts`, `grid`, `predicate` and
	 * `viewMode` out of `content` and `concepts`. Only the fields nobody on this screen edits come from it.
	 */
	| { type: "documentRefreshed"; document: SmartWorkspaceDTO };

/**
 * Actions that touch `ui` only, and therefore can never reach the PUT.
 *
 * None of these has a controller method today: view code writes the atom directly, which is why two of
 * them hand-roll the sync and the analytics the observer would have done. `setPage` genuinely persists
 * nothing (the DTO has no field for the current page) and genuinely does not sync today — that is
 * behaviour to keep, not an omission to fix.
 */
export type EphemeralAction =
	| { type: "setSearch"; search: string }
	| { type: "setSelection"; selection: string[] }
	| { type: "setPage"; page: number }
	| { type: "setResultTotal"; total: number };

export type WorkspaceActionType = WorkspaceAction["type"];

export type ActionOf<TType extends WorkspaceActionType> = Extract<WorkspaceAction, { type: TType }>;

/**
 * Whether the action enqueues the whole-document PUT.
 *
 * Keyed on the action type and `satisfies`-guarded, so a new action cannot be added without answering the
 * question. Where the answer depends on the payload it is a predicate rather than a boolean, and the two
 * cases are not a design choice — they are today's behaviour.
 *
 * The sweep used to live here too, per action, because today's set is not uniform. It does not any more:
 * an unreferenced private concept is now an invariant violation, so the sweep runs after **every** content
 * action and there is nothing per-action left to state.
 */
export type ActionSpec = {
	persists: boolean | ((action: never) => boolean);
};

export const actionSpec = {
	addColumn: { persists: (action: ActionOf<"addColumn">) => !("property" in action) },
	// `updateSharedColumn` and `updateSharedCustomAttributeColumn` were the two methods the observer excluded,
	// precisely because a shared concept lives outside the workspace document. They change nothing in
	// `content`, so there is nothing to persist.
	editColumn: { persists: (action: ActionOf<"editColumn">) => !("property" in action) && action.shared !== true },
	// Asks for the rename and changes no content, so there is nothing to send.
	renameTagOption: { persists: false },
	removeColumn: { persists: true },
	// See the note on the action: a mid-drag sample is not a layout anyone has settled on, and one PUT per
	// frame of a resize is what persisting it would cost. The settled layout arrives without the flag, and
	// that is the dispatch this persists.
	reorderColumns: { persists: (action: ActionOf<"reorderColumns">) => action.fromGrid !== true },
	addFilter: { persists: true },
	upsertFilter: { persists: true },
	editFilter: { persists: true },
	removeFilter: { persists: true },
	removeAllFilters: { persists: true },
	addChart: { persists: true },
	editChart: { persists: true },
	removeChart: { persists: true },
	reorderCharts: { persists: true },
	// `persist: false` is the deep link — see the action. Everything else remembers the choice.
	setView: { persists: (action: ActionOf<"setView">) => action.persist !== false },
	setSort: { persists: true },
	setPageSize: { persists: true },
	// The shared half of the pool has its own API; replacing it from that API's response is not a change
	// to the workspace document and did not PUT one before either (`sharedConceptService` wrote the atom and
	// stopped there).
	sharedConceptsReplaced: { persists: false },
	// The one write-behind that cannot be local-first: a custom attribute's `conceptId` is assigned by the
	// backend, so `addColumn {property}` holds a placeholder until this lands. It carries the PUT that
	// `addColumn {property}` therefore does not, which keeps today's ordering — the workspace document is
	// never persisted referencing a concept the shared API has not accepted yet.
	conceptReconciled: { persists: true },
	// The transaction has already rewritten the criterion in the stored document of every workspace of every
	// user, this one included, so both sides hold the new value and a PUT here would overwrite the document
	// the same transaction just wrote.
	tagOptionRenamed: { persists: false },
	// It came *from* upstream. Persisting it would PUT a document nobody edited — and, since every PUT
	// invalidates the query this action is fed by, it would be a loop.
	documentRefreshed: { persists: false },
	setSearch: { persists: false },
	setSelection: { persists: false },
	setPage: { persists: false },
	setResultTotal: { persists: false },
} satisfies Record<WorkspaceActionType, ActionSpec>;

/** Whether the action enqueues the whole-document PUT. */
export function actionPersists(action: WorkspaceAction): boolean {
	const answer = actionSpec[action.type].persists;
	return typeof answer === "function" ? (answer as (a: WorkspaceAction) => boolean)(action) : answer;
}

/**
 * The ephemeral action types, as a runtime witness of the union.
 *
 * Listed rather than derived because the *default has to be the safe one*: a new action is treated as
 * content — swept, invariant-checked — until someone says otherwise here, and `satisfies` makes sure every
 * ephemeral action is named.
 */
const ephemeralActionTypes = {
	setSearch: true,
	setSelection: true,
	setPage: true,
	setResultTotal: true,
} satisfies Record<EphemeralAction["type"], true>;

export function isEphemeralAction(action: WorkspaceAction): action is EphemeralAction {
	return action.type in ephemeralActionTypes;
}
